import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import type { Participant, ParticipantGender, ParticipantSelfUpdateInput } from 'shared';
import { useCurrentParticipant, useUpdateMe } from '@/hooks/useAuth';

const GENDER_OPTIONS: ReadonlyArray<{ value: ParticipantGender; label: string }> = [
  { value: 'F', label: 'Feminino' },
  { value: 'M', label: 'Masculino' },
  { value: 'I', label: 'Outro' },
];

/** RFC3339 (e.g. 2005-06-15T00:00:00Z) → YYYY-MM-DD for the date input. */
function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Trim and map empty strings to null so we don't persist "" where null fits. */
function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export default function AccountSettingsPage() {
  const { data: participant, isLoading, isError } = useCurrentParticipant();

  return (
    <>
      <Helmet>
        <title>Definições da conta | AICD</title>
      </Helmet>
      <div className="space-y-6">
        <header className="space-y-3">
          <Link
            to="/profile"
            aria-label="Voltar ao perfil"
            className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <BackGlyph />
            Voltar
          </Link>
          <div>
            <h1 className="text-2xl font-black text-foreground">Definições da conta</h1>
            <p className="mt-1 subtitle text-sm text-muted-foreground">
              Atualize os seus dados pessoais.
            </p>
          </div>
        </header>

        {isLoading && (
          <div className="flex items-center justify-center h-48" role="status" aria-live="polite">
            <div className="text-muted-foreground">A carregar…</div>
          </div>
        )}

        {isError && !isLoading && (
          <div
            role="alert"
            className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
          >
            Não foi possível carregar os seus dados. Tente novamente mais tarde.
          </div>
        )}

        {!isLoading && !isError && participant && (
          <AccountSettingsForm participant={participant} />
        )}
      </div>
    </>
  );
}

function AccountSettingsForm({ participant }: { participant: Participant }) {
  const navigate = useNavigate();
  const update = useUpdateMe();

  const [nome, setNome] = useState(participant.nome);
  const [apelido, setApelido] = useState(participant.apelido);
  const [genero, setGenero] = useState<ParticipantGender>(participant.genero);
  const [dataNascimento, setDataNascimento] = useState(toDateInputValue(participant.dataNascimento));
  const [telemovel, setTelemovel] = useState(participant.telemovel ?? '');
  const [numeroCc, setNumeroCc] = useState(participant.numeroCc ?? '');
  const [habilitacoesLiterarias, setHabilitacoesLiterarias] = useState(
    participant.habilitacoesLiterarias ?? '',
  );
  const [situacaoLaboral, setSituacaoLaboral] = useState(participant.situacaoLaboral ?? '');
  const [escola, setEscola] = useState(participant.escola ?? '');
  const [morada, setMorada] = useState(participant.morada ?? '');
  const [localidade, setLocalidade] = useState(participant.localidade ?? '');
  const [eeNome, setEeNome] = useState(participant.eeNome ?? '');
  const [eeTelemovel, setEeTelemovel] = useState(participant.eeTelemovel ?? '');
  const [eeEmail, setEeEmail] = useState(participant.eeEmail ?? '');
  const [eeNumeroCc, setEeNumeroCc] = useState(participant.eeNumeroCc ?? '');
  const [autorizacaoImagem, setAutorizacaoImagem] = useState(participant.autorizacaoImagem);
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!nome.trim() || !apelido.trim()) {
      setFormError('O nome e o apelido são obrigatórios.');
      return;
    }
    if (!dataNascimento) {
      setFormError('A data de nascimento é obrigatória.');
      return;
    }

    const payload: ParticipantSelfUpdateInput = {
      nome: nome.trim(),
      apelido: apelido.trim(),
      genero,
      dataNascimento,
      telemovel: nullable(telemovel),
      numeroCc: nullable(numeroCc),
      habilitacoesLiterarias: nullable(habilitacoesLiterarias),
      situacaoLaboral: nullable(situacaoLaboral),
      escola: nullable(escola),
      eeNome: nullable(eeNome),
      eeTelemovel: nullable(eeTelemovel),
      eeEmail: nullable(eeEmail),
      eeNumeroCc: nullable(eeNumeroCc),
      morada: nullable(morada),
      localidade: nullable(localidade),
      autorizacaoImagem,
    };

    update.mutate(payload, { onSuccess: () => navigate('/profile') });
  };

  const hasBannerError = !!formError || update.isError;

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} aria-busy={update.isPending} noValidate>
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {hasBannerError && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
            {formError || 'Não foi possível guardar as alterações. Tente novamente.'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="nome" className="label">
            Nome
          </label>
          <input
            id="nome"
            type="text"
            autoComplete="given-name"
            required
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="apelido" className="label">
            Apelido
          </label>
          <input
            id="apelido"
            type="text"
            autoComplete="family-name"
            required
            className="input"
            value={apelido}
            onChange={(e) => setApelido(e.target.value)}
          />
        </div>
      </div>

      <fieldset>
        <legend className="label">Género</legend>
        <div className="mt-1 flex flex-wrap gap-4" role="radiogroup" aria-required="true">
          {GENDER_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm font-medium text-foreground"
            >
              <input
                type="radio"
                name="genero"
                value={option.value}
                checked={genero === option.value}
                onChange={() => setGenero(option.value)}
                className="accent-primary"
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="dataNascimento" className="label">
          Data de nascimento
        </label>
        <input
          id="dataNascimento"
          type="date"
          autoComplete="bday"
          required
          className="input"
          value={dataNascimento}
          onChange={(e) => setDataNascimento(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="email" className="label">
          Email (não editável)
        </label>
        <input
          id="email"
          type="email"
          className="input"
          value={participant.email ?? ''}
          readOnly
          disabled
        />
      </div>

      <div>
        <label htmlFor="telemovel" className="label">
          Telemóvel
        </label>
        <input
          id="telemovel"
          type="tel"
          autoComplete="tel"
          className="input"
          value={telemovel}
          onChange={(e) => setTelemovel(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="numeroCc" className="label">
          Nº do Cartão de Cidadão
        </label>
        <input
          id="numeroCc"
          type="text"
          className="input"
          value={numeroCc}
          onChange={(e) => setNumeroCc(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="habilitacoesLiterarias" className="label">
          Habilitações literárias
        </label>
        <input
          id="habilitacoesLiterarias"
          type="text"
          className="input"
          value={habilitacoesLiterarias}
          onChange={(e) => setHabilitacoesLiterarias(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="situacaoLaboral" className="label">
          Situação laboral
        </label>
        <input
          id="situacaoLaboral"
          type="text"
          className="input"
          value={situacaoLaboral}
          onChange={(e) => setSituacaoLaboral(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="escola" className="label">
          Escola
        </label>
        <input
          id="escola"
          type="text"
          className="input"
          value={escola}
          onChange={(e) => setEscola(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="morada" className="label">
          Morada
        </label>
        <input
          id="morada"
          type="text"
          autoComplete="street-address"
          className="input"
          value={morada}
          onChange={(e) => setMorada(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="localidade" className="label">
          Localidade
        </label>
        <input
          id="localidade"
          type="text"
          className="input"
          value={localidade}
          onChange={(e) => setLocalidade(e.target.value)}
        />
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Encarregado de educação
          </h2>
          <p className="mt-1 form-hint">Preencha estes dados se for menor de idade.</p>
        </div>

        <div>
          <label htmlFor="eeNome" className="label">
            Nome
          </label>
          <input
            id="eeNome"
            type="text"
            className="input"
            value={eeNome}
            onChange={(e) => setEeNome(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="eeTelemovel" className="label">
            Telemóvel
          </label>
          <input
            id="eeTelemovel"
            type="tel"
            className="input"
            value={eeTelemovel}
            onChange={(e) => setEeTelemovel(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="eeEmail" className="label">
            Email
          </label>
          <input
            id="eeEmail"
            type="email"
            className="input"
            value={eeEmail}
            onChange={(e) => setEeEmail(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="eeNumeroCc" className="label">
            Nº do Cartão de Cidadão
          </label>
          <input
            id="eeNumeroCc"
            type="text"
            className="input"
            value={eeNumeroCc}
            onChange={(e) => setEeNumeroCc(e.target.value)}
          />
        </div>
      </section>

      <label className="flex items-start gap-3 text-sm font-medium text-foreground">
        <input
          type="checkbox"
          checked={autorizacaoImagem}
          onChange={(e) => setAutorizacaoImagem(e.target.checked)}
          className="mt-0.5 accent-primary"
        />
        Autorizo a utilização da minha imagem.
      </label>

      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          disabled={update.isPending}
          className="flex-1 rounded-xl border border-border bg-card px-4 py-3 text-[15px] font-semibold text-foreground transition-colors hover:bg-accent active:bg-accent/80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancelar
        </button>
        <button type="submit" disabled={update.isPending} className="btn-primary flex-1">
          {update.isPending ? 'A guardar…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

function BackGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
